import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Home, Waves, Wrench, Building2, FlaskConical, ArrowRight } from 'lucide-react';

const services = [
  {
    anchor: 'whole-house-systems',
    image: `${process.env.PUBLIC_URL}/whole-house-water-filter.png`,
    icon: Home,
    title: 'Whole House Systems',
    intro: 'A whole-house filtration and conditioning system treats water at the point of entry, so every tap, shower, and appliance in your home receives clean, treated water. Custom-designed for your specific water chemistry, these systems reduce contaminants while protecting your plumbing, water heaters, and skin from the effects of hard water.',
    features: [
      'Whole-home point-of-entry treatment',
      'Custom filtration based on water chemistry',
      'Salt-based and salt-free softening options',
      'Reduction of chlorine, sediment, odors, hardness, and other water-quality concerns',
      'Protection for plumbing, fixtures, water heaters, and appliances',
      'Professional installation by a licensed plumber',
      'Minimal-maintenance options'
    ],
    cta: 'Request a Whole House System Evaluation',
  },
  {
    anchor: 'reverse-osmosis',
    image: `${process.env.PUBLIC_URL}/aquify-reverse-osmosis.png`,
    icon: Waves,
    title: 'Reverse Osmosis Systems',
    intro: 'Reverse osmosis delivers ultra-pure drinking water by pushing water through a semi-permeable membrane, removing a wide range of dissolved contaminants. Ideal for drinking and cooking water at the kitchen sink.',
    features: [
      'Compact under-sink installation',
      'Removes dissolved solids, heavy metals, nitrates, and more',
      'High-efficiency systems with minimal water waste',
      'Crystal-clear, great-tasting drinking water on demand',
      'Tankless options available',
    ],
    cta: 'Request a Reverse Osmosis Evaluation',
  },
  {
    anchor: 'well-water-treatment',
    image: `${process.env.PUBLIC_URL}/well-water-treatment.png`,
    icon: Wrench,
    title: 'Well Water Treatment',
    intro: "Well water presents unique challenges — iron, sulfur, bacteria, sediment, and pH imbalances are all common. Our well water specialists design treatment systems specific to your well's water chemistry.",
    features: [
      'Comprehensive well water testing and analysis',
      'Iron and manganese removal systems',
      'Hydrogen sulfide (sulfur odor) treatment',
      'Bacteria and coliform disinfection solutions',
      'Sediment and turbidity filtration',
      'Ongoing maintenance plans available',
    ],
    cta: 'Request a Well Water Evaluation',
  },
  {
    anchor: 'commercial-filtration',
    image: `${process.env.PUBLIC_URL}/commercial-water-treatment-system.png`,
    icon: Building2,
    title: 'Commercial Filtration',
    intro: 'From restaurants and cafes to medical facilities and light manufacturing, Aquify designs and installs professional-grade water treatment systems built for commercial demands and code compliance.',
    features: [
      'High-capacity filtration for commercial water volumes',
      'Designed to meet local health and building code requirements',
      'Restaurant and food service water filtration',
      'Ice machine and beverage system filtration',
      'PFAS and forever chemical treatment',
      'Service agreements and maintenance contracts available',
    ],
    cta: 'Request a Commercial Water Evaluation',
  },
  {
    anchor: 'drinking-water-systems',
    image: `${process.env.PUBLIC_URL}/water-evaluation.png`,
    icon: FlaskConical,
    title: 'Drinking Water Systems',
    intro: 'Aquify offers point-of-use drinking water systems designed to improve the quality, taste, and clarity of water directly at the tap. These compact systems can be installed beneath a sink or inline with a dedicated drinking-water faucet, refrigerator, ice maker, or other point-of-use application.',
    features: [
      'Inline drinking-water filtration',
      'Under-sink point-of-use systems',
      'Dedicated faucet options',
      'Refrigerator and ice-maker filtration',
      'Reduction of chlorine, taste, odor, sediment, and selected contaminants',
      'Compact professional installation',
      'Filter replacement and maintenance options',
    ],
    cta: 'Request a Drinking Water System Evaluation',
  }
];

const ServicesImageMockupPage = () => {
  const getGradient = (index) => {
    return index % 2 === 0
      ? "bg-gradient-to-br from-[#1B4B75] to-[#2379A4]"
      : "bg-gradient-to-br from-[#2379A4] to-[#35A0CF]";
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="py-16 bg-slate-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#1571BA' }}>
            Design • Sales • Installation • Service
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
            Our Water Treatment Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Certified water treatment solutions designed for your home, business, and unique water quality needs.
          </p>
          <a
            href="/contact"
            className="aq-primary-btn inline-block font-bold text-lg px-10 py-4 rounded-xl transform hover:scale-105 shadow-md"
          >
            Request a Complimentary Water Evaluation
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.anchor}
                  className="rounded-2xl flex flex-col h-full shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-white/10 card-gradient-md-2"
                >
                  {/* Image Area */}
                  <div className="w-full h-64 sm:h-72 bg-white">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Content Area */}
                  <div className="p-8 md:p-10 flex flex-col flex-1">
                    {/* Section header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15 border border-white/25">
                        <Icon className="h-7 w-7 text-[#BDEBFA]" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">{service.title}</h2>
                    </div>

                    <p className="text-lg text-white/85 leading-relaxed mb-8">{service.intro}</p>

                    <ul className="space-y-3 mb-8 flex-1">
                      {service.features.map((feat, fi) => (
                        <li key={fi} className="flex items-start gap-3">
                          <span
                            className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0 bg-[#8EDAF4]"
                          />
                          <span className="text-white/90 text-base">{feat}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/contact"
                      className="bg-white text-[#111827] border border-[#111827] hover:bg-gray-100 inline-flex items-center justify-center gap-2 font-bold px-7 py-3.5 rounded-lg transform transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1571BA] shadow w-full sm:w-auto self-start mt-auto"
                    >
                      {service.cta}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
            Ready to Request Your Complimentary Water Evaluation?
          </h2>
          <p className="text-[#E3F2FD] text-lg mb-8 leading-relaxed">
            Our team will contact you to review your request and confirm your appointment window. No obligation, no pressure.
          </p>
          <a
            href="/contact"
            className="aq-primary-btn inline-block font-bold text-lg px-10 py-4 rounded-xl transform hover:scale-105 shadow-lg"
            style={{ border: '2px solid white' }}
          >
            Submit Evaluation Request
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesImageMockupPage;
